import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { api } from '../api/client';
import { storage } from '../lib/storage';
import { useTheme } from '../context/ThemeContext';
import fieldiqIcon from '../assets/fieldiq-icon.png';
import fieldiqIconLight from '../assets/fieldiq-icon-light.png';
import fieldiqWordmark from '../assets/fieldiq-wordmark.png';
import fieldiqWordmarkLight from '../assets/fieldiq-wordmark-light.png';

export default function InviteSetupScreen() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const { theme } = useTheme();
  const icon = theme === 'light' ? fieldiqIconLight : fieldiqIcon;
  const wordmark = theme === 'light' ? fieldiqWordmarkLight : fieldiqWordmark;

  const [validating, setValidating] = useState(true);
  const [inviteData, setInviteData] = useState<{ email: string; name: string; companyName: string } | null>(null);
  const [inviteError, setInviteError] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setInviteError('No invite token provided.');
      setValidating(false);
      return;
    }
    api.auth.validateInvite(token)
      .then((res) => {
        // The API answers `{ success, data: { email, name, companyName } }`.
        // Reading `res.data` stored the envelope, so the welcome card showed
        // "Team Member", no company and no email — the invitee was never told
        // which address the account is under. `'data' in body`, not
        // `body.data || body`: a null data must not fall through to the envelope.
        const body = res.data;
        setInviteData(body && typeof body === 'object' && 'data' in body ? body.data : body);
      })
      .catch((err) => {
        setInviteError(err.response?.data?.error || 'This invite link is invalid or has expired.');
      })
      .finally(() => setValidating(false));
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await api.auth.setupPassword({ token, password });
      const newToken = res.data.token;
      if (newToken) {
        // `storage.setToken`, not `localStorage.setItem`. On a Capacitor build
        // the token is read back from Preferences (`storage.getToken`), so a
        // raw localStorage write lands where nothing looks: the reload below
        // would hit `checkAuth`, find no token, and drop a brand-new user onto
        // the login screen right after "You're all set!". Harmless on web,
        // where the two are the same call — but this screen is the onboarding
        // path, and it must not depend on which platform it runs on.
        await storage.setToken(newToken);
      }
      setSuccess(true);
      setTimeout(() => {
        window.location.href = '/';
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to set password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-full bg-fieldiq-dark flex flex-col items-center justify-center p-4">
        <Loader2 className="w-8 h-8 text-fieldiq-accent animate-spin" />
        <p className="text-fieldiq-text mt-3">Validating your invite...</p>
      </div>
    );
  }

  if (inviteError) {
    return (
      <div className="min-h-full bg-fieldiq-dark flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-sm text-center">
          <img src={icon} alt="FieldIQ" className="w-16 h-16 rounded-2xl mx-auto mb-4" />
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-white mb-2">Invalid Invite</h2>
          <p className="text-fieldiq-text text-sm mb-6">{inviteError}</p>
          <p className="text-fieldiq-muted text-xs">Ask your admin to resend the invite.</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-full bg-fieldiq-dark flex flex-col items-center justify-center p-4">
        <CheckCircle className="w-12 h-12 text-green-400 mb-3" />
        <h2 className="text-lg font-bold text-white mb-1">You're all set!</h2>
        <p className="text-fieldiq-text text-sm">Redirecting to your dashboard...</p>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-fieldiq-dark flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src={icon} alt="FieldIQ" className="w-20 h-20 rounded-2xl mx-auto mb-4" />
          <img src={wordmark} alt="FieldIQ" className="h-8 mx-auto mb-2" />
          <p className="text-fieldiq-text">Set Up Your Account</p>
        </div>

        {/* Welcome info */}
        <div className="bg-fieldiq-card border border-fieldiq-border rounded-lg p-4 mb-6">
          <p className="text-sm text-white font-medium">Welcome, {inviteData?.name || 'Team Member'}!</p>
          <p className="text-xs text-fieldiq-text mt-1">
            You've been invited to join <span className="text-fieldiq-accent">{inviteData?.companyName}</span>. Create a password to get started.
          </p>
          <p className="text-xs text-fieldiq-muted mt-1">{inviteData?.email}</p>
        </div>

        {/* Password Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* The account's address, for the password manager only. With no
              username field on the form, a saved password is filed under no
              account (or the wrong one) and the login page autofills a stale
              one. Read-only, out of the tab order, and never sent: the save
              posts only the token and the password. */}
          <input
            type="email"
            name="username"
            autoComplete="username"
            value={inviteData?.email || ''}
            readOnly
            hidden
            tabIndex={-1}
            aria-hidden="true"
          />
          {error && (
            <div className="bg-fieldiq-danger/20 border border-fieldiq-danger text-fieldiq-danger px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="new-password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-fieldiq-card border border-fieldiq-border rounded-lg pl-4 pr-12 py-3 text-white focus:border-fieldiq-accent focus:outline-none"
                placeholder="Min. 8 characters"
                required
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-fieldiq-text"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Confirm Password</label>
            <div className="relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                name="confirm-password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full bg-fieldiq-card border border-fieldiq-border rounded-lg pl-4 pr-12 py-3 text-white focus:border-fieldiq-accent focus:outline-none"
                placeholder="Re-enter password"
                required
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-fieldiq-text"
              >
                {showConfirm ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-fieldiq-accent text-fieldiq-dark font-bold py-3 rounded-lg hover:bg-fieldiq-accent/90 transition disabled:opacity-50"
          >
            {isSubmitting ? 'Setting up...' : 'Create Account'}
          </button>
        </form>
      </div>
    </div>
  );
}
